export const hasText = texts => (texts || []).some(text => !!text.value?.trim());

export const getLocalizedValue = (texts, language) => {
    if (!texts?.length) return '';
    return texts.find(t => t.language === language && t.value)?.value ?? texts.find(t => t.value)?.value ?? '';
};

export const setLocalizedValue = (texts, language, value) => {
    const copy = (texts || []).slice();
    const index = copy.findIndex(t => t.language === language);
    if (index === -1) {
        copy.push({ language, value });
    } else {
        copy[index] = { ...copy[index], value };
    }
    return copy;
};

export const getDocumentLanguages = document => {
    const languages = (document?.names || []).filter(n => n.value).map(n => n.language);
    return [...new Set(languages)];
};

export const createSection = (language, name = '') => ({
    id: crypto.randomUUID(),
    sortOrder: 0,
    names: [{ language, value: name }],
    descriptions: [],
    items: [],
});

export const createItem = language => ({
    id: crypto.randomUUID(),
    sortOrder: 0,
    names: [{ language, value: '' }],
    descriptions: [],
    priceAmount: null,
    imageUrl: '',
    isHidden: false,
    attributes: null,
    optionsEnabled: false,
    options: [],
});

export const createOption = language => ({
    id: crypto.randomUUID(),
    sortOrder: 0,
    names: [{ language, value: '' }],
    priceAmount: null,
    isHidden: false,
});

// Legacy documents have no optionsEnabled flag — treat items that already carry options as enabled.
export const areOptionsEnabled = item => item.optionsEnabled ?? (item.options || []).length > 0;

const readableValue = value => value.toLowerCase().replaceAll('_', ' ');

export const buildItemAiContext = (item, defaultLanguage) => {
    const lines = [`Menu item: ${getLocalizedValue(item.names, defaultLanguage)}`];
    if (areOptionsEnabled(item)) {
        const optionNames = (item.options || [])
            .map(option => getLocalizedValue(option.names, defaultLanguage))
            .filter(Boolean);
        if (optionNames.length) lines.push(`Options: ${optionNames.join(', ')}`);
    }
    const attributes = item.attributes || {};
    const parts = [];
    if (attributes.spiciness) parts.push(`spiciness: ${readableValue(attributes.spiciness)}`);
    if (attributes.dietaryRestrictions?.length)
        parts.push(attributes.dietaryRestrictions.map(readableValue).join(', '));
    if (attributes.preparationMethods?.length) {
        parts.push(`preparation: ${attributes.preparationMethods.map(readableValue).join(', ')}`);
    }
    if (attributes.allergens?.length) parts.push(`allergens: ${attributes.allergens.map(readableValue).join(', ')}`);
    const ingredients = getLocalizedValue(attributes.ingredients, defaultLanguage);
    if (ingredients) parts.push(`ingredients: ${ingredients}`);
    if (attributes.servesNumPeople) parts.push(`serves ${attributes.servesNumPeople}`);
    if (parts.length) lines.push(`Attributes: ${parts.join('; ')}`);
    return lines.join('\n');
};

export const buildSectionAiContext = (section, defaultLanguage) => {
    const lines = [`Menu section: ${getLocalizedValue(section.names, defaultLanguage)}`];
    const itemNames = (section.items || []).map(item => getLocalizedValue(item.names, defaultLanguage)).filter(Boolean);
    if (itemNames.length) lines.push(`Items in the section: ${itemNames.join(', ')}`);
    return lines.join('\n');
};

export const buildMenuAiContext = (document, cuisines, defaultLanguage) => {
    const lines = [`Menu: ${getLocalizedValue(document?.names, defaultLanguage)}`];
    if (cuisines?.length) lines.push(`Cuisines: ${cuisines.map(readableValue).join(', ')}`);
    const sectionNames = (document?.sections || [])
        .map(section => getLocalizedValue(section.names, defaultLanguage))
        .filter(Boolean);
    if (sectionNames.length) lines.push(`Sections: ${sectionNames.join(', ')}`);
    return lines.join('\n');
};

// Rewrite sort orders from array positions before saving.
export const normalizeDocument = document => ({
    ...document,
    sections: (document.sections || []).map((section, sectionIndex) => ({
        ...section,
        sortOrder: sectionIndex,
        items: (section.items || []).map((item, itemIndex) => ({
            ...item,
            sortOrder: itemIndex,
            isHidden: false,
            optionsEnabled: areOptionsEnabled(item),
            options: (item.options || []).map((option, optionIndex) => ({
                ...option,
                sortOrder: optionIndex,
                isHidden: false,
            })),
        })),
    })),
});
